import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Bot, Hand, ShoppingBag, DollarSign, Activity, Sparkles, ArrowRight, AlertTriangle,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { getBusiness, getStats, seedDemo } from "@/lib/api";
import { dayjs, money, ORDER_STATUS } from "@/lib/format";

function Kpi({ icon: Icon, label, value, hint, tone = "emerald", testId, delay = 0 }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-600", sky: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600", rose: "bg-rose-50 text-rose-600", slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div
      data-testid={testId}
      style={{ animationDelay: `${delay}ms` }}
      className="animate-rise bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200"
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 font-display text-3xl font-extrabold text-[#111B21]" data-testid={`${testId}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function Onboarding({ onSeed, seeding }) {
  const steps = [
    { to: "/entrenar", t: "Entrena al bot", d: "Nombre, horarios, preguntas frecuentes" },
    { to: "/catalogo", t: "Sube tu catálogo", d: "Productos y precios que el bot puede vender" },
    { to: "/simulador", t: "Pruébalo en el simulador", d: "Chatea como cliente y mira cómo responde" },
    { to: "/ajustes", t: "Conecta WhatsApp", d: "Autoriza tu número en Meta" },
  ];
  return (
    <div data-testid="onboarding-card" className="animate-rise bg-[#103529] text-white rounded-3xl p-7 lg:p-9 mb-8 relative overflow-hidden">
      <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-emerald-400/20" />
      <div className="relative grid lg:grid-cols-[1fr_1.4fr] gap-8 items-center">
        <div>
          <Sparkles className="h-6 w-6 text-emerald-300" />
          <h2 className="mt-3 font-display text-2xl font-extrabold">Pon tu chatbot en marcha en 4 pasos</h2>
          <p className="mt-2 text-sm text-emerald-100/80">
            ¿Quieres verlo funcionando ya? Carga un negocio de ejemplo (cafetería) con catálogo y respuestas rápidas.
          </p>
          <Button data-testid="seed-demo-button" onClick={onSeed} disabled={seeding}
            className="mt-5 rounded-full bg-emerald-400 hover:bg-emerald-300 text-[#103529] font-bold">
            {seeding ? "Cargando…" : "Cargar datos de ejemplo"}
          </Button>
        </div>
        <ol className="grid sm:grid-cols-2 gap-3">
          {steps.map((s, i) => (
            <li key={s.to}>
              <Link to={s.to} data-testid={`onboarding-step-${i + 1}`}
                className="group flex gap-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 p-4 transition-colors">
                <span className="h-7 w-7 shrink-0 rounded-full bg-emerald-400 text-[#103529] text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block font-semibold text-sm">{s.t}</span>
                  <span className="block text-xs text-emerald-100/70 mt-0.5">{s.d}</span>
                </span>
                <ArrowRight className="h-4 w-4 ml-auto mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useOutletContext();
  const [s, setS] = useState(null);
  const [biz, setBiz] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = () => Promise.all([getStats(), getBusiness()]).then(([a, b]) => { setS(a); setBiz(b); });
  useEffect(() => { load().catch(() => toast.error("No se pudieron cargar las estadísticas")); }, []);

  const onSeed = async () => {
    setSeeding(true);
    try { await seedDemo(); await load(); toast.success("Datos de ejemplo cargados"); }
    catch { toast.error("No se pudieron cargar los datos"); }
    finally { setSeeding(false); }
  };

  const cur = biz?.currency || "USD";
  const daily = (s?.daily || []).map((d) => ({ ...d, label: dayjs(d.date).format("dd D") }));

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="home-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <PageHeader
          title={`Hola, ${user.name?.split(" ")[0] || ""}`}
          subtitle={biz?.name ? `Así va el WhatsApp de ${biz.name} hoy.` : "Así va tu WhatsApp hoy."}
        >
          <Link to="/chats"><Button data-testid="go-chats-button" className="rounded-full bg-emerald-500 hover:bg-emerald-600">Abrir chats</Button></Link>
        </PageHeader>

        {biz && !biz.name && <Onboarding onSeed={onSeed} seeding={seeding} />}

        {s && s.needs_human > 0 && (
          <Link to="/chats" data-testid="needs-human-alert"
            className="animate-rise mb-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800 hover:bg-amber-100 transition-colors">
            <Hand className="h-5 w-5" />
            <span className="text-sm font-semibold">{s.needs_human} conversación(es) esperan a un asesor humano</span>
            <ArrowRight className="h-4 w-4 ml-auto" />
          </Link>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testId="kpi-conversations" icon={MessageCircle} label="Conversaciones" value={s?.total_conversations ?? "–"} hint={`${s?.new_conversations_today ?? 0} nuevas hoy`} />
          <Kpi testId="kpi-messages-today" icon={Activity} tone="sky" label="Mensajes hoy" value={s?.messages_today ?? "–"} hint={`${s?.customer_messages ?? 0} de clientes en total`} delay={60} />
          <Kpi testId="kpi-bot-resolution" icon={Bot} tone="emerald" label="Resuelto por el bot" value={s ? `${s.bot_resolution_rate}%` : "–"} hint={`${s?.bot_messages ?? 0} respuestas automáticas`} delay={120} />
          <Kpi testId="kpi-revenue" icon={DollarSign} tone="amber" label="Ventas registradas" value={s ? money(s.revenue, cur) : "–"} hint={`${s?.orders_total ?? 0} pedidos / reservas`} delay={180} />
        </div>

        <div className="mt-6 grid lg:grid-cols-[2fr_1fr] gap-4">
          <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="chart-messages">
            <h3 className="font-display text-lg font-bold">Mensajes de los últimos 7 días</h3>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} barGap={2}>
                  <CartesianGrid vertical={false} stroke="#EEF0F2" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} width={30} />
                  <Tooltip cursor={{ fill: "#F0F2F5" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB" }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="customer" name="Clientes" fill="#64748B" maxBarSize={28} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="bot" name="Bot" fill="#10B981" maxBarSize={28} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="agent" name="Asesores" fill="#0EA5E9" maxBarSize={28} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col gap-6">
            <div data-testid="orders-by-status">
              <h3 className="font-display text-lg font-bold">Pedidos por estado</h3>
              <ul className="mt-3 space-y-2">
                {Object.entries(ORDER_STATUS).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className={`px-2.5 py-0.5 rounded-full border text-xs font-semibold ${v.cls}`}>{v.label}</span>
                    <span className="font-bold">{s?.orders_by_status?.[k] || 0}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="top-products">
              <h3 className="font-display text-lg font-bold">Más vendidos</h3>
              {(s?.top_products || []).length === 0 && <p className="mt-2 text-sm text-slate-400">Aún no hay ventas.</p>}
              <ul className="mt-3 space-y-2">
                {(s?.top_products || []).map((p) => (
                  <li key={p.name} className="flex justify-between text-sm"><span className="truncate">{p.name}</span><span className="font-bold">{p.qty}</span></li>
                ))}
              </ul>
            </div>
            {s?.failed_messages > 0 && (
              <Link to="/ajustes" data-testid="failed-messages-alert" className="flex gap-2 text-xs text-rose-700 bg-rose-50 rounded-xl p-3 hover:bg-rose-100 transition-colors">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {s.failed_messages} mensajes no se entregaron por WhatsApp. Ver cómo solucionarlo.
              </Link>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
